/**
 * Document comments — page-level comment threads on uploaded documents.
 *
 * Tables: document_comment_threads / document_comments /
 * document_comment_mentions / document_comment_thread_activity
 * (franklin-data-pipeline migrations 20260914102705 + 20260914102709).
 *
 * The raw `*Row` shapes are what PostgREST returns for the explicit column
 * lists in `repository.ts`; the domain shapes are what the UI renders, derived
 * by `mappers.ts`.
 */

import type { UserAvatarUser } from "@/components/ui/user-avatar";

// ---------------------------------------------------------------------------
// Raw rows
// ---------------------------------------------------------------------------

export interface ProfileNameRow {
  first_name: string | null;
  last_name: string | null;
}

export interface AuthorProfileRow {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  avatar_url: string | null;
  color: string | null;
}

export interface CommentRow {
  id: number;
  thread_id: number;
  body: string;
  is_root: boolean;
  author_id: number;
  created_at: string;
  modified_at: string | null;
  deleted_at: string | null;
  /** Null when the author's profile is outside the caller's read scope. */
  author: AuthorProfileRow | null;
  mentions: Array<{ mentioned_user_id: number; read_at: string | null }>;
}

export interface LinkedIssueRow {
  id: number;
  issue_number: number;
  title: string;
  status: string;
  building_project_id: number;
  deleted_at: string | null;
}

/** Where a thread's comments live: an issue-backed thread reads and writes
 *  `pm_issue_comments` on its sub-issue; an opted-out thread uses
 *  `document_comments`. */
export type CommentSource = "document" | "pm";

export interface ThreadRow {
  id: number;
  document_id: number;
  building_id: number;
  project_id: number | null;
  page_number: number | null;
  resolved_at: string | null;
  resolved_by: number | null;
  linked_issue_id: number | null;
  created_at: string;
  created_by: number;
  modified_at: string | null;
  deleted_at: string | null;
  resolved_by_profile: ProfileNameRow | null;
  created_by_profile: ProfileNameRow | null;
  linked_issue: LinkedIssueRow | null;
  document: { id: number; version: number | null; filename: string } | null;
  /** Filled by the repository: the sub-issue's comments for a linked thread
   *  (converted to this shape), the document comments otherwise. */
  comments: CommentRow[];
  comment_source: CommentSource;
}

export interface ThreadActivityRow {
  id: number;
  thread_id: number;
  action_type: string;
  actor_id: number | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface ThreadLookupRow {
  id: number;
  document_id: number;
  building_id: number;
  page_number: number | null;
  resolved_at: string | null;
  deleted_at: string | null;
}

export interface CountsRow {
  document_id: number;
  open_count: number;
  resolved_count: number;
}

// ---------------------------------------------------------------------------
// Domain shapes
// ---------------------------------------------------------------------------

/** The document a panel or rail is opened on — the fields the viewer's
 *  preview `Document` does not carry (`version_group`, `project_id`, …). */
export interface DocumentCommentScope {
  id: number;
  version: number | null;
  versionGroup: string | null;
  projectId: number | null;
  buildingId: number;
  orgId: string;
  filename: string;
  isLatestVersion: boolean | null;
  isDeleted: boolean;
}

/** One version in the scope's lineage, as listed by the panel. */
export interface DocumentVersionRef {
  id: number;
  version: number | null;
  filename: string;
  isDeleted: boolean;
}

export type DocumentThreadActionType =
  | "thread_created"
  | "comment_added"
  | "comment_edited"
  | "comment_deleted"
  | "resolved"
  | "reopened"
  | "issue_created"
  | "thread_deleted";

export const DOCUMENT_THREAD_ACTION_TYPES: readonly DocumentThreadActionType[] = [
  "thread_created",
  "comment_added",
  "comment_edited",
  "comment_deleted",
  "resolved",
  "reopened",
  "issue_created",
  "thread_deleted",
];

export interface DocumentComment {
  id: number;
  threadId: number;
  body: string;
  authorId: number;
  /** Null when the profile is not readable; render a neutral placeholder. */
  author: UserAvatarUser | null;
  createdAt: string;
  modifiedAt: string | null;
  mentionedUserIds: number[];
  /** True when the viewing user is mentioned here and has not read it yet. */
  unreadForMe: boolean;
}

export interface DocumentThreadLinkedIssue {
  id: number;
  number: number;
  title: string;
  status: string;
  projectId: number;
}

export interface DocumentThread {
  id: number;
  documentId: number;
  documentVersion: number | null;
  documentFilename: string | null;
  buildingId: number;
  projectId: number | null;
  pageNumber: number | null;
  createdAt: string;
  createdById: number;
  createdByName: string | null;
  resolvedAt: string | null;
  resolvedById: number | null;
  resolvedByName: string | null;
  linkedIssue: DocumentThreadLinkedIssue | null;
  commentSource: CommentSource;
  root: DocumentComment;
  replies: DocumentComment[];
}

export interface DocumentCommentCounts {
  open: number;
  resolved: number;
}

export interface DocumentThreadActivity {
  id: number;
  threadId: number;
  actionType: DocumentThreadActionType;
  actorId: number | null;
  actorName: string | null;
  createdAt: string;
  metadata: Record<string, unknown>;
}

export interface DocumentThreadLookup {
  id: number;
  documentId: number;
  buildingId: number;
  pageNumber: number | null;
  resolved: boolean;
}

export interface MentionAudienceUser {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  avatar_url: string | null;
  color: string | null;
}

/**
 * The comment schema is not there yet — the app has shipped ahead of the
 * franklin-data-pipeline migration. Surfaces render "Comments are not
 * available yet." rather than a generic failure.
 */
export class CommentsUnavailableError extends Error {
  constructor(message = "Document comments are not available on this database yet") {
    super(message);
    this.name = "CommentsUnavailableError";
  }
}
